import React, { useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { DeepChat } from "deep-chat-react";
import { useTranslation } from "react-i18next";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useFetch } from "../../hooks/fetch";
import ACTIONS from "../../store/action";
import ReactDOMServer from "react-dom/server";
import ErrorMessage from "../customComponent/customMessages/ErrorMessage";
import {
  DeepChatStyle,
  DeepChatMessagesStyle,
  DeepChatTextInputPlaceholderStyle,
  FloatingDeepChatTextInputPlaceholderStyle,
  DeepChatTextInputContainerStyle,
  DeepChatSubmitButtonLoadingStyle,
  DeepChatSubmitButtonStyle
} from "./Styles";
import {
  FloatingDeepChatStyle,
  FloatingDeepChatTextInputContainerStyle,
  FloatingDeepChatSubmitButtonStyle,
  FloatingDeepChatSubmitButtonLoadingStyle
} from "./FloatingChat/Styles.js";
import { registerMessageActionsWebComponent } from "./MessageActions";
import { sendMessageToAIChatBot, getAIChatBotConversationHistory, createAIChatBotConversation } from "../../api/api";
import { IntroPanel } from "./IntroPanel";
import { FloatingIntroPanel } from "./FloatingChat/FloatingIntroPanel.js";
import {
  NEW_CHAT_ID,
  CHAT_ROLES,
  USER_MESSAGE_CHARACTER_LIMIT,
  MAX_USER_MESSAGES_TO_SEND_TO_SERVER,
  PENDO_EVENTS
} from "./Constants";
import SubmitButton from "../../assets/svg/aiChatBotSubmit.js";
import SubmitButtonPurple from "../../assets/svg/aiChatBotSubmitPurple.js";
import { useDashboardBucketsTrackingEventStable as useDashboardBucketsTrackingEvent } from "../../utils/pendo/hooks";
import { FloatingChatToggleButtonComponent } from "./minimizeChatComponent";
import { useHistory } from "react-router-dom";

const getHtmlContent = (conversationId, message, isFloatingMode = false) => {
  // Encode message content to prevent HTML injection issues
  const encodedContent = encodeURIComponent(message.text);
  return `<message-actions message-id="${message.id}" message-content="${encodedContent}" conversation-id="${conversationId}" message-feedback="${message.feedback}" is-floating-mode="${isFloatingMode}"></message-actions>`;
};

const getHtmlErrorMessage = (t) => {
  const errorMessage = t("ai_chat_bot.errors.failed_processing_request");
  const iconHtml = ReactDOMServer.renderToString(<ExclamationCircleOutlined />);

  const errorHtml = `<div style="text-align: center; line-height: 150%; display: flex; align-items: flex-start;">
    <span style="width: auto; font-size: 18px; margin-right: 8px; line-height: 150%;">${iconHtml}</span>
    <span style="width: auto; font-size: 16px; line-height: 150%;">${errorMessage}</span>
  </div>`;

  return errorHtml;
};

const ChatWidgetComponent = ({
  selectedConversationId,
  shouldLoadHistory,
  clearChatHistory,
  handleAfterConversationCreated,
  handleAfterLoadingSelectedConversation,
  onChatCleared,
  isFloatingMode = false,
  cachedHistory,
  onStreamingStateChange
}) => {
  const user = useSelector((state) => state.user);
  const userId = user?.oktaId;
  const history = useHistory();
  const dispatch = useDispatch();
  const store = useStore();
  const isIntroPanelShown = useSelector((state) => state.aiChatBot.isIntroPanelShown);
  const { t } = useTranslation();
  const chatElementRef = useRef();
  const conversationCreatedRef = useRef(false);
  const [getConversationHistory, conversationHistoryResult, , conversationHistoryError] = useFetch(
    getAIChatBotConversationHistory
  );
  const { trackEvent } = useDashboardBucketsTrackingEvent();

  // Store conversation ID in a ref instead of state to avoid re-renders
  const activeConversationIdRef = useRef(selectedConversationId);
  const chatKey = activeConversationIdRef?.current ?? NEW_CHAT_ID;

  const isStreamingRef = useRef(false);

  const updateStreamingState = useCallback((isStreaming) => {
    isStreamingRef.current = isStreaming;

    dispatch(ACTIONS.setChatStreaming(isStreaming));

    if (onStreamingStateChange) {
      onStreamingStateChange(isStreaming);
    }
  }, []);

  const handleFloatingChatToggle = () => {
    if (activeConversationIdRef.current) {
      dispatch(ACTIONS.setFloatingChatConversationId(activeConversationIdRef.current));

      dispatch(ACTIONS.setFloatingChatShouldLoadHistory(true));
    }

    dispatch(ACTIONS.setFloatingChatVisible(true));

    // Track Pendo event for collapsing to floating chat
    trackEvent(PENDO_EVENTS.CHAT_COLLAPSED_TO_FLOATING);

    history.push("/");
  };

  const saveMessageToFloatingChat = useCallback(
    (conversationId, message) => {
      if (isFloatingMode && conversationId) {
        const state = store.getState();
        const existingHistory = state.floatingChat.conversationHistory[conversationId] || [];
        const updatedHistory = [...existingHistory, message];
        dispatch(ACTIONS.setFloatingChatHistory(conversationId, updatedHistory));
      }
    },
    [isFloatingMode]
  );

  const scrollToBottom = useCallback(() => {
    const shadowRoot = chatElementRef.current?.shadowRoot;
    if (!shadowRoot) {
      return;
    }

    let messagesContainer = null;

    const elements = shadowRoot.querySelectorAll('div[style*="height"]');
    for (const element of elements) {
      if (element.scrollHeight > element.clientHeight && !element.scrollTop) {
        messagesContainer = element;
        break;
      }
    }

    if (messagesContainer) {
      messagesContainer.scrollTop = Math.min(messagesContainer.scrollTop + 100, messagesContainer.scrollHeight);

      let parent = messagesContainer.parentElement;
      while (parent && parent !== shadowRoot) {
        if (!parent.scrollTop) {
          parent.scrollTop = Math.min(parent.scrollTop + 100, parent.scrollHeight);
        }
        parent = parent.parentElement;
      }
    }
  }, []);

  useEffect(() => {
    window.scrollChatToBottom = scrollToBottom;
    return () => {
      delete window.scrollChatToBottom;
    };
  }, [scrollToBottom]);

  useEffect(() => {
    // Update the ref when URL param changes
    if (selectedConversationId && Number(selectedConversationId) !== Number(activeConversationIdRef.current)) {
      activeConversationIdRef.current = selectedConversationId;
    }
  }, [selectedConversationId]);

  useEffect(() => {
    registerMessageActionsWebComponent(); // Register Web Component on mount
  }, []);

  useEffect(() => {
    if (chatElementRef.current && selectedConversationId && shouldLoadHistory) {
      getConversationHistory(userId, selectedConversationId);
      activeConversationIdRef.current = selectedConversationId;
    }
  }, [shouldLoadHistory, selectedConversationId]);

  useEffect(() => {
    const isNewConversationUrl = location.pathname.includes("new");
    if (!isNewConversationUrl) {
      dispatch(ACTIONS.setAiChatBotIntroPanelShown(false));
    }
  }, []);

  useEffect(() => {
    if (chatElementRef.current && clearChatHistory) {
      chatElementRef.current.clearMessages();

      if (isFloatingMode && activeConversationIdRef.current) {
        dispatch(ACTIONS.setFloatingChatHistory(activeConversationIdRef.current, []));
      }

      activeConversationIdRef.current = null;
      conversationCreatedRef.current = false;
      onChatCleared();
    }
  }, [clearChatHistory, onChatCleared, isFloatingMode]);

  useEffect(() => {
    if (conversationHistoryError) {
      ErrorMessage(t("ai_chat_bot.errors.failed_fetching_conversation_history"));
    }
  }, [conversationHistoryError]);

  useEffect(() => {
    let observer;
    const interval = setInterval(() => {
      const shadowRoot = chatElementRef.current?.shadowRoot;
      if (!shadowRoot) return;

      const input = shadowRoot.querySelector("#input");
      if (input) {
        input.style.visibility = isIntroPanelShown ? "hidden" : "visible";
      }

      observer = new MutationObserver(() => {
        const input = shadowRoot.querySelector("#input");
        if (input) {
          input.style.visibility = isIntroPanelShown ? "hidden" : "visible";
        }
      });

      observer.observe(shadowRoot, {
        childList: true,
        subtree: true
      });

      clearInterval(interval);
    }, 50);

    return () => {
      clearInterval(interval);
      if (observer) observer.disconnect();
    };
  }, [isIntroPanelShown]);

  useEffect(() => {
    if (conversationHistoryResult) {
      chatElementRef.current.loadHistory = () => {
        const conversationId = activeConversationIdRef.current;
        const messages = conversationHistoryResult.data.messages || [];
        return messages.map((msg) => {
          if (msg.role === CHAT_ROLES.AI) {
            const html = getHtmlContent(conversationId, msg, isFloatingMode);
            return {
              role: msg.role,
              text: msg.text,
              html
            };
          }
          return msg;
        });
      };

      handleAfterLoadingSelectedConversation(conversationHistoryResult.data.messages || []);
    }
  }, [conversationHistoryResult]);

  useEffect(() => {
    if (isFloatingMode && cachedHistory && chatElementRef.current && activeConversationIdRef.current) {
      chatElementRef.current.loadHistory = () => {
        const conversationId = activeConversationIdRef.current;
        return cachedHistory.map((msg) => {
          if (msg.role === CHAT_ROLES.AI) {
            const html = getHtmlContent(conversationId, msg, isFloatingMode);
            return {
              role: msg.role,
              text: msg.text,
              html
            };
          }
          return msg;
        });
      };
    }
  }, [isFloatingMode, cachedHistory, activeConversationIdRef.current]);

  const handleSuggestedQuestionClick = useCallback(
    (questionText) => {
      trackEvent(PENDO_EVENTS.INTRO_PANEL_OPTION_SELECTED, { questionText });
      dispatch(ACTIONS.setAiChatBotIntroPanelShown(false));
      if (chatElementRef.current) {
        setTimeout(() => {
          chatElementRef.current.submitUserMessage({ text: questionText });
        }, 100);
      }
    },
    [dispatch]
  );

  const createConversationId = async () => {
    try {
      const result = await createAIChatBotConversation();
      return result.data;
    } catch (error) {
      trackEvent(PENDO_EVENTS.TEAMMATE_AI_ERROR_MESSAGE, {
        type: "conversationId creation error",
        message: error.message
      });
      ErrorMessage(t("ai_chat_bot.errors.failed_creating_conversation"));
    }
  };

  const sendMessage = useCallback(
    async (body, signals) => {
      // Set streaming state to true when streaming starts
      updateStreamingState(true);

      let currentConversationId = activeConversationIdRef.current;
      let conversationCreated = false;
      try {
        if (!currentConversationId && !conversationCreatedRef.current) {
          currentConversationId = await createConversationId();
          if (currentConversationId) {
            activeConversationIdRef.current = currentConversationId;
            conversationCreatedRef.current = true;
            conversationCreated = true;
          } else {
            trackEvent(PENDO_EVENTS.TEAMMATE_AI_ERROR_MESSAGE, {
              type: "conversationId is undefined"
            });
            signals.onResponse({
              role: "ai_error",
              html: getHtmlErrorMessage(t)
            });
            return;
          }
        }

        const lastMessage = body.messages[0];
        trackEvent(PENDO_EVENTS.QUESTION_SUBMITTED, { conversationId: currentConversationId });

        if (isFloatingMode) {
          saveMessageToFloatingChat(currentConversationId, {
            role: CHAT_ROLES.USER,
            text: lastMessage.text,
            id: Date.now() // Generate a temporary ID for user messages
          });
        }

        const response = await sendMessageToAIChatBot(currentConversationId, lastMessage);

        // we call it after the response received to make sure conversation subject is set
        if (conversationCreated) {
          handleAfterConversationCreated(currentConversationId);
        }
        signals.onOpen();

        try {
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          let messageId = null;
          let isDone = false;
          let fullText = "";

          while (!isDone) {
            const { done, value } = await reader.read();
            if (done) {
              isDone = true;
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n\n");
            buffer = lines.pop();

            for (const line of lines) {
              const data = parseSSEData(currentConversationId, line);
              if (!data) {
                continue;
              }
              if (data?.error) {
                const errorHtml = getHtmlErrorMessage(t);
                signals.onResponse({
                  role: "ai_error",
                  html: errorHtml,
                  overwrite: true
                });
                return;
              }
              if (data?.text !== undefined) {
                fullText += data.text;
                await signals.onResponse({
                  text: data.text,
                  overwrite: false
                });
              }
              if (data?.id) {
                messageId = data.id;
              }
            }
          }

          const html = getHtmlContent(
            currentConversationId,
            {
              text: fullText,
              id: messageId,
              feedback: null
            },
            isFloatingMode
          );

          chatElementRef.current.addMessage({
            role: CHAT_ROLES.AI,
            html
          });

          if (isFloatingMode) {
            saveMessageToFloatingChat(currentConversationId, {
              role: CHAT_ROLES.AI,
              text: fullText,
              id: messageId,
              feedback: null
            });
          }

          // Set streaming state to false when streaming ends
          updateStreamingState(false);

          signals.onClose();

          trackEvent(PENDO_EVENTS.ANSWER_DISPLAYED, { answerId: messageId, conversationId: currentConversationId });
        } catch (streamError) {
          // Set streaming state to false when streaming fails
          updateStreamingState(false);

          trackEvent(PENDO_EVENTS.TEAMMATE_AI_ERROR_MESSAGE, {
            type: "Stream error",
            message: streamError.message,
            conversationId: currentConversationId
          });
          signals.onResponse({ error: streamError.message });
          signals.onClose();
        }
      } catch (error) {
        // Set streaming state to false when there's a server error
        updateStreamingState(false);

        if (conversationCreated) {
          handleAfterConversationCreated(currentConversationId);
        }

        trackEvent(PENDO_EVENTS.TEAMMATE_AI_ERROR_MESSAGE, {
          type: "server error",
          message: error.message,
          conversationId: currentConversationId
        });

        const errorHtml = getHtmlErrorMessage(t);

        signals.onResponse({
          role: "ai_error",
          html: errorHtml
        });
      }
    },
    [handleAfterConversationCreated, clearChatHistory, user]
  );
  function parseSSEData(conversationId, sseEvent) {
    if (!sseEvent.startsWith("data: ")) {
      return null;
    }

    try {
      const eventData = JSON.parse(sseEvent.slice(6));
      return eventData;
    } catch (parseError) {
      trackEvent(PENDO_EVENTS.SSE_PARSING_ERROR, {
        message: parseError.message,
        messageId: null,
        conversationId
      });
      return null;
    }
  }

  return user ? (
    <div
      style={{
        display: "flex",
        gap: "10px",
        top: "20px",
        flexDirection: "column",
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        position: "relative"
      }}
    >
      {!isFloatingMode && (
        <div
          style={{
            position: "relative",
            width: "100%",
            left: "calc(100% - 32px)",
            zIndex: 1000
          }}
        >
          <FloatingChatToggleButtonComponent onFloatingChatToggle={handleFloatingChatToggle} />
        </div>
      )}
      <DeepChat
        style={isFloatingMode ? FloatingDeepChatStyle : DeepChatStyle}
        ref={chatElementRef}
        key={chatKey}
        connect={{
          handler: sendMessage,
          stream: true
        }}
        requestBodyLimits={{
          maxMessages: MAX_USER_MESSAGES_TO_SEND_TO_SERVER
        }}
        onError={(error) => {
          trackEvent(PENDO_EVENTS.TEAMMATE_AI_ERROR_MESSAGE, {
            type: "DeepChat error",
            message: error
          });
        }}
        errorMessages={{
          displayServiceErrorMessages: true
        }}
        messageStyles={DeepChatMessagesStyle}
        submitButtonStyles={{
          alwaysEnabled: true,
          loading: {
            container: {
              default: isFloatingMode ? FloatingDeepChatSubmitButtonStyle : DeepChatSubmitButtonStyle
            }
          },
          submit: {
            container: {
              default: isFloatingMode ? FloatingDeepChatSubmitButtonLoadingStyle : DeepChatSubmitButtonLoadingStyle
            },
            svg: {
              content: isFloatingMode ? SubmitButtonPurple : SubmitButton
            }
          }
        }}
        textInput={{
          styles: {
            container: isFloatingMode ? FloatingDeepChatTextInputContainerStyle : DeepChatTextInputContainerStyle
          },
          placeholder: {
            text: t(isFloatingMode ? "ai_chat_bot.floating_message_placeholder" : "ai_chat_bot.message_placeholder"),
            style: isFloatingMode ? FloatingDeepChatTextInputPlaceholderStyle : DeepChatTextInputPlaceholderStyle
          },
          characterLimit: USER_MESSAGE_CHARACTER_LIMIT
        }}
      >
        {isFloatingMode ? (
          <FloatingIntroPanel />
        ) : (
          <IntroPanel onSuggestedQuestionClick={handleSuggestedQuestionClick} />
        )}
      </DeepChat>
    </div>
  ) : null;
};

export { ChatWidgetComponent as ChatWidget };
